/**
 * Turns a failure into words a form filler can act on.
 *
 * @remarks
 * This module is deliberately provider-agnostic. It takes a DESCRIPTION of a
 * failure rather than an error object, so nothing here has to know about
 * `@ns/http-request` or `@ns/plugin-arm-proxy-client`. A picker that does know
 * its provider classifies its own error and hands the result over; the words
 * live here so all pickers say the same thing about the same situation.
 *
 * What it never does is name a status code. A number in a message tells the
 * filler nothing he can act on, and it leaks how the call was made.
 *
 * @packageDocumentation
 */

/**
 * The coarse families a failed call falls into.
 *
 * @remarks
 * Carries the same nine members as `HttpFailureKind` in `@ns/http-request`, by
 * VALUE rather than by import: this package sits above any particular
 * transport, so it must not depend on one. Nothing checks the two lists against
 * each other. The caller that maps its own error onto this union is where a
 * member added there would surface, because that mapping is exhaustive.
 *
 * What each value means:
 *
 * - `'abort'` for a call that was cancelled, usually because the filler moved on.
 * - `'attempt-timeout'` for one try that ran out of time, with retries left.
 * - `'http'` for an upstream that answered with a status we treat as a failure.
 * - `'network'` for a network that never came back with an answer.
 * - `'parse'` for an answer that was not the shape it claimed to be.
 * - `'timeout'` for the whole call running out of time.
 * - `'too-large'` for an answer bigger than we are willing to read.
 * - `'unsupported'` for a request that was wrong before it was sent.
 * - `'validation'` for an answer that parsed but did not hold up.
 */
export type FailureKind =
  | 'abort'
  | 'attempt-timeout'
  | 'http'
  | 'network'
  | 'parse'
  | 'timeout'
  | 'too-large'
  | 'unsupported'
  | 'validation';

/** A failure, as the picker that caught it describes it. */
export interface Failure {
  /** Which coarse family it belongs to. */
  kind: FailureKind;
  /**
   * The `error.code` of a refusal by the proxy, when the caller recognised one.
   *
   * @remarks
   * A picker gets this from `isProxyRefusal` plus the body's `error.code`. It
   * is a plain string here so this package needs no dependency on the proxy
   * client. A code with no words of its own falls through to the status.
   */
  refusalCode?: string;
  /**
   * The status an upstream answered with, when `kind` is `'http'`.
   *
   * @remarks
   * Read to CHOOSE the words, never printed in them.
   */
  status?: number;
}

/**
 * One sentence per kind. A `Record` over the union rather than a `switch`, so
 * adding a tenth kind is a compile error here instead of a silent fallthrough.
 */
const BY_KIND: Record<FailureKind, string> = {
  abort: 'The request was cancelled before it finished.',
  'attempt-timeout': 'That attempt took too long, so it was given up on.',
  http: 'The service could not give this list right now.',
  network: 'The service could not be reached. Check your connection.',
  parse: 'The service sent something that could not be read.',
  timeout: 'The service did not answer in time. Try again in a moment.',
  'too-large': 'The answer was too large to read. Narrow the search by typing.',
  unsupported: 'This request cannot be made as it is written.',
  validation: 'The service sent something unexpected, so it was not used.',
};

/** Words for the statuses worth telling apart. Everything else uses the kind. */
const BY_STATUS = new Map<number, string>([
  [401, 'You are not signed in for this list. Sign in and try again.'],
  [403, 'You do not have access to this list. Ask for permission first.'],
  [404, 'That list does not exist at the service.'],
  [429, 'Too many requests were made. Wait a moment and try again.'],
  [502, 'The service is not reachable right now. Try again in a moment.'],
  [503, 'The service is not available right now. Try again in a moment.'],
  [504, 'The service took too long to answer. Try again in a moment.'],
]);

/**
 * Words for a refusal by the proxy. Only `auth-unavailable` is temporary, so
 * only that one invites another attempt; the other four need the filler or an
 * administrator to do something first.
 */
const BY_REFUSAL = new Map<string, string>([
  [
    'no-credential',
    'You are not signed in for this list. Sign in and try again.',
  ],
  ['expired', 'Your session has expired. Sign in again to see this list.'],
  [
    'consent-revoked',
    'Access to this list was withdrawn. Ask for permission again.',
  ],
  [
    'identity-mismatch',
    'This session belongs to a different account. Sign in again.',
  ],
  [
    'auth-unavailable',
    'Signing in is not available right now. Try again in a moment.',
  ],
]);

/**
 * The sentence to show for a failure.
 *
 * @remarks
 * Most specific wins: a refusal the proxy named says more than the status it
 * arrived with, and a status says more than the family. Anything unrecognised
 * falls back to the family, so there is always a sentence.
 *
 * @param failure - What the picker caught, already classified by it.
 * @returns One sentence in plain words, naming no status code.
 */
export function failureMessage(failure: Failure): string {
  const { kind, refusalCode, status } = failure;

  if (refusalCode !== undefined) {
    const refusalWords = BY_REFUSAL.get(refusalCode);
    if (refusalWords !== undefined) return refusalWords;
  }

  if (status !== undefined) {
    const statusWords = BY_STATUS.get(status);
    if (statusWords !== undefined) return statusWords;
  }

  return BY_KIND[kind];
}
